import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { idbGet, idbSet } from '../../shared/utils/idb-storage.util';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  private templatesList = new BehaviorSubject<any[]>([]);
  private quoteStages = new BehaviorSubject<any[]>(this.getLocal('quoteStages'));
  private integrations = new BehaviorSubject<any[]>(this.getLocal('integrations'));
  private businessDataArr = new BehaviorSubject<any[]>(this.getLocal('businessDataArr'));

  templatesList$ = this.templatesList.asObservable();
  quoteStages$ = this.quoteStages.asObservable();
  integrations$ = this.integrations.asObservable();
  businessDataArr$ = this.businessDataArr.asObservable();

  constructor() {
    this.initTemplatesList();
  }

  private async initTemplatesList(): Promise<void> {
    // 1. Check for legacy data in localStorage and migrate it to IndexedDB
    try {
      const localData = localStorage.getItem('templatesList');
      if (localData) {
        const parsed = JSON.parse(localData);
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.templatesList.next(parsed);
          await idbSet('templatesList', parsed);
        }
        localStorage.removeItem('templatesList');
        return;
      }
    } catch (e) {
      console.warn('Error reading legacy templatesList from localStorage:', e);
      try {
        localStorage.removeItem('templatesList');
      } catch (err) {}
    }

    // 2. Load cached template list from IndexedDB asynchronously
    try {
      const idbData = await idbGet<any[]>('templatesList');
      if (Array.isArray(idbData)) {
        this.templatesList.next(idbData);
      }
    } catch (e) {
      console.warn('Error loading templatesList from IndexedDB:', e);
    }
  }

  getTemplatesListValue(): any[] {
    return this.templatesList.getValue();
  }

  getQuoteStagesValue(): any[] {
    return this.quoteStages.getValue();
  }

  getIntegrationsValue(): any[] {
    return this.integrations.getValue();
  }

  getBusinessDataArrValue(): any[] {
    return this.businessDataArr.getValue();
  }

  private sanitizeIntegrations(data: any[]): any[] {
    if (!Array.isArray(data)) return [];
    return data.map((item) => {
      if (!item) return item;
      const copy = { ...item };
      if (Array.isArray(copy.keysConnections)) {
        copy.keysConnections = copy.keysConnections.map((conn: any) => {
          if (!conn) return conn;
          const connCopy = { ...conn };
          delete connCopy.secretKey;
          delete connCopy.authToken;
          return connCopy;
        });
      }
      return copy;
    });
  }

  private getLocal(key: string): any[] {
    try {
      const data = localStorage.getItem(key);
      if (!data) return [];
      const parsed = JSON.parse(data);
      if (key === 'integrations' && Array.isArray(parsed)) {
        const sanitized = this.sanitizeIntegrations(parsed);
        // If sanitized purged any legacy secretKey, rewrite sanitized data to localStorage
        if (JSON.stringify(parsed) !== JSON.stringify(sanitized)) {
          this.setLocal('integrations', sanitized);
        }
        return sanitized;
      }
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  private setLocal(key: string, value: any[]) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn('Failed to store in localStorage:', key, e);
    }
  }

  setTemplatesList(data: any[]) {
    this.templatesList.next(data);
    // Remove from localStorage to immediately free up origin quota
    try {
      localStorage.removeItem('templatesList');
    } catch (e) {}
    // Asynchronously persist to IndexedDB off the main thread
    idbSet('templatesList', data);
  }

  setQuoteStages(data: any[]) {
    this.quoteStages.next(data);
    this.setLocal('quoteStages', data);
  }

  setIntegrations(data: any[]) {
    const sanitized = this.sanitizeIntegrations(data);
    this.integrations.next(sanitized);
    this.setLocal('integrations', sanitized);
  }

  setBusinessDataArr(data: any[]) {
    this.businessDataArr.next(data);
    this.setLocal('businessDataArr', data);
  }
}
